<template>
  <div class="hero">
    <div class="overlay"></div>

    <!-- DEADLINE -->
    <div v-if="jobs.length" class="deadline">
      <v-icon small color="white" class="mr-2">mdi-calendar-clock</v-icon>
      <p>
        Interested and qualified applicants should signify their interest and
        upload the required documents not later than
        <strong v-if="currentDateTo">{{ formatDate(currentDateTo) }}.</strong>
      </p>
    </div>

    <template v-if="jobs.length">
      <div
        class="carousel-container"
        @touchstart.passive="onTouchStart"
        @touchend.passive="onTouchEnd"
      >
        <button
          class="nav-btn nav-btn--side left"
          aria-label="Previous position"
          @click="prev"
        >
          <v-icon color="white">mdi-chevron-left</v-icon>
        </button>

        <div class="viewport">
          <div
            class="track"
            :style="{ transform: 'translateX(' + translateX + 'px)' }"
          >
            <article
              v-for="(job, index) in jobs"
              :key="job.id || index"
              class="card"
              :class="getClass(index)"
              @click="handleCardClick(index)"
            >
              <!-- HEAD -->
              <header class="card-head">
                <span class="eyebrow">
                  Position {{ index + 1 }} of {{ jobs.length }}
                </span>
                <h3>{{ job.position_title || "Administrative Aide II" }}</h3>
                <div class="chips">
                  <span v-if="job.salary_grade != 0" class="chip">
                    SG {{ job.salary_grade }}
                  </span>
                  <span class="chip chip--salary">
                    <small>{{
                      job.salary_grade == 0 ? "Salary" : "Monthly"
                    }}</small>
                    ₱ {{ display(job.monthly_salary) }}
                  </span>
                </div>
              </header>

              <!-- BODY (scrolls) -->
              <div class="body">
                <div class="details">
                  <div
                    v-for="f in infoFields(job)"
                    :key="f.label"
                    class="info-item"
                  >
                    <v-icon small color="#2fd6a1">{{ f.icon }}</v-icon>
                    <div>
                      <div class="white--text" style="text-align: start">
                        {{ f.label + " :" }}
                      </div>
                      <div class="white--text">{{ f.value }}</div>
                    </div>
                  </div>
                </div>

                <div class="details">
                  <div
                    v-for="s in sections(job)"
                    :key="s.title"
                    class="section"
                  >
                    <h4>
                      <v-icon x-small color="#2fd6a1">{{ s.icon }}</v-icon>
                      {{ s.title }}
                    </h4>
                    <div class="rich" v-html="s.html"></div>
                  </div>
                </div>
              </div>

              <!-- APPLY -->
              <button
                class="apply-btn"
                :tabindex="index === current ? 0 : -1"
                @click.stop="dataPrivacy(job)"
              >
                Apply Now
                <v-icon small color="white">mdi-arrow-right</v-icon>
              </button>
            </article>
          </div>
        </div>

        <button
          class="nav-btn nav-btn--side right"
          aria-label="Next position"
          @click="next"
        >
          <v-icon color="white">mdi-chevron-right</v-icon>
        </button>
      </div>

      <!-- CONTROLS -->
      <div class="controls">
        <button
          class="nav-btn nav-btn--inline"
          aria-label="Previous position"
          @click="prev"
        >
          <v-icon color="white">mdi-chevron-left</v-icon>
        </button>

        <div class="dots">
          <button
            v-for="(j, i) in jobs"
            :key="i"
            class="dot"
            :class="{ active: i === current }"
            :aria-label="'Go to position ' + (i + 1)"
            @click="goTo(i)"
          ></button>
        </div>

        <button
          class="nav-btn nav-btn--inline"
          aria-label="Next position"
          @click="next"
        >
          <v-icon color="white">mdi-chevron-right</v-icon>
        </button>
      </div>
    </template>

    <!-- EMPTY / LOADING -->
    <div v-else class="empty">
      <v-progress-circular
        v-if="loading"
        indeterminate
        color="white"
      ></v-progress-circular>
      <template v-else>
        <div class="empty-state">
          <div class="empty-icon-wrap">
            <v-icon size="44" color="#1e8a39"
              >mdi-briefcase-search-outline</v-icon
            >
          </div>
          <h3 class="empty-title">No open positions right now</h3>
          <p class="empty-text">
            There are no job postings at the moment. New openings will appear
            here once they're published, so please check back soon.
          </p>
          <div class="empty-actions">
            <v-btn
              rounded
              depressed
              color="#1e8a39"
              dark
              class="text-none"
              @click="loadJobs"
            >
              <v-icon left size="18">mdi-refresh</v-icon>
              Refresh
            </v-btn>
          </div>
        </div>
      </template>
    </div>

    <!-- DATA PRIVACY DIALOG -->
    <v-dialog
      v-model="showDataPrivacy"
      max-width="640"
      scrollable
      persistent
      transition="dialog-bottom-transition"
    >
      <v-card class="dialog-card">
        <div class="privacy-header">
          <span class="privacy-badge">
            <v-icon color="white" size="26">mdi-shield-lock</v-icon>
          </span>
          <div>
            <div class="privacy-head-title">Data Privacy Statement</div>
            <div class="privacy-head-sub">
              Davao del Norte State College, RA 10173
            </div>
          </div>
        </div>

        <v-card-text class="pa-0">
          <div class="privacy-modal px-6 py-5">
            <div class="privacy-item">
              <span class="privacy-icon">
                <v-icon color="green darken-3" small
                  >mdi-account-details</v-icon
                >
              </span>
              <div>
                <div class="privacy-title">What we collect</div>
                <p>
                  Davao del Norte State College (DNSC) will gather your name,
                  email address, contact numbers, and other personal details.
                  These details are collected for the purpose of your
                  application for a vacant position.
                </p>
              </div>
            </div>

            <div class="privacy-item">
              <span class="privacy-icon">
                <v-icon color="green darken-3" small>mdi-database-lock</v-icon>
              </span>
              <div>
                <div class="privacy-title">How it is stored</div>
                <p>
                  The personal information and other requested credentials will
                  be kept for as long as necessary to fulfill the intended
                  purpose. DNSC ensures that this information and documents
                  remain secure and accessible solely to authorized individuals.
                </p>
              </div>
            </div>

            <div class="privacy-item">
              <span class="privacy-icon">
                <v-icon color="green darken-3" small>mdi-delete-sweep</v-icon>
              </span>
              <div>
                <div class="privacy-title">Disposal</div>
                <p>
                  When it comes time for disposal, it will be done securely to
                  prevent further processing, unauthorized access, or disclosure
                  to any third party or the public.
                </p>
              </div>
            </div>

            <div class="privacy-item">
              <span class="privacy-icon">
                <v-icon color="green darken-3" small>mdi-gavel</v-icon>
              </span>
              <div>
                <div class="privacy-title">Legal basis</div>
                <p>
                  All actions are taken in strict adherence to the provisions of
                  RA 10173, also known as the Data Privacy Act of 2012, and its
                  implementing rules.
                </p>
              </div>
            </div>

            <v-alert
              text
              dense
              color="green darken-3"
              border="left"
              icon="mdi-information-outline"
              class="mt-2 mb-0"
            >
              By continuing, you express your consent for DNSC to collect,
              process, store, and dispose of your personal information in
              accordance with RA 10173 and its regulations. All provided
              information and uploaded credentials will be handled with the
              utmost confidentiality. Thank you.
            </v-alert>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="px-6 py-4">
          <v-spacer></v-spacer>
          <v-btn
            text
            rounded
            color="grey darken-1"
            class="text-none"
            @click="showDataPrivacy = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="green darken-3"
            depressed
            rounded
            class="px-6 white--text text-none"
            @click="proceedApply"
          >
            <v-icon left small>mdi-check-circle-outline</v-icon>
            I agree
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  data() {
    return {
      selectedJob: null,
      current: 0,
      containerWidth: 0,
      cardWidth: 720,
      gap: 80,
      job: null,
      showDataPrivacy: false,
      jobs: [],
      loading: true,
      touchStartX: 0,
    };
  },

  mounted() {
    if (this.$route.params && this.$route.params.job) {
      this.job = this.$route.params.job;
    }
    this.initialize();
    window.addEventListener("resize", this.updateSizes);
    window.addEventListener("keydown", this.onKey);
  },

  // Vue 2 hook (beforeUnmount never fires in Vue 2)
  beforeDestroy() {
    window.removeEventListener("resize", this.updateSizes);
    window.removeEventListener("keydown", this.onKey);
  },

  computed: {
    currentDateTo() {
      const j = this.jobs[this.current];
      return (j && j.date_to) || null;
    },
    translateX() {
      return (
        (this.containerWidth - this.cardWidth) / 2 -
        this.current * (this.cardWidth + this.gap)
      );
    },
  },

  methods: {
    initialize() {
      this.loading = true;
      this.axiosCall("/job-posting/DnscPage/active", "GET")
        .then((res) => {
          if (res && res.data && res.data.length > 0) {
            this.jobs = res.data;
            this.current = 0;
            // measure AFTER the cards are rendered
            this.$nextTick(this.updateSizes);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    dataPrivacy(job) {
      this.job = job;
      this.showDataPrivacy = true;
    },

    handleCardClick(index) {
      const cls = this.getClass(index);
      if (cls === "right") this.next();
      else if (cls === "left") this.prev();
    },

    // REDIRECT AFTER AGREE
    proceedApply() {
      this.showDataPrivacy = false;
      localStorage.setItem("jobData", JSON.stringify(this.job));
      const cleanTitle = this.job.position_title.replace(/[\s()]/g, "");
      this.$router.push({
        path: "/jobrequirements/?Position=" + cleanTitle,
      });
    },

    updateSizes() {
      const viewport = this.$el.querySelector(".viewport");
      const card = this.$el.querySelector(".card");
      const track = this.$el.querySelector(".track");
      if (!viewport || !card || !track) return;

      const styles = getComputedStyle(viewport);
      const pl = parseFloat(styles.paddingLeft) || 0;
      const pr = parseFloat(styles.paddingRight) || 0;

      this.containerWidth = viewport.clientWidth - pl - pr;
      this.cardWidth = card.offsetWidth;
      this.gap = parseFloat(getComputedStyle(track).gap) || 80;
    },

    next() {
      if (!this.jobs.length) return;
      this.current = this.current < this.jobs.length - 1 ? this.current + 1 : 0;
    },

    prev() {
      if (!this.jobs.length) return;
      this.current = this.current > 0 ? this.current - 1 : this.jobs.length - 1;
    },

    goTo(i) {
      this.current = i;
    },

    getClass(index) {
      if (index === this.current) return "active";
      if (index === this.current - 1) return "left";
      if (index === this.current + 1) return "right";
      return "hidden";
    },

    onTouchStart(e) {
      this.touchStartX = e.changedTouches[0].clientX;
    },
    onTouchEnd(e) {
      const dx = e.changedTouches[0].clientX - this.touchStartX;
      if (Math.abs(dx) > 50) dx < 0 ? this.next() : this.prev();
    },
    onKey(e) {
      if (this.showDataPrivacy) return;
      const t = e.target && e.target.tagName;
      if (t === "INPUT" || t === "TEXTAREA") return;
      if (e.key === "ArrowRight") this.next();
      if (e.key === "ArrowLeft") this.prev();
    },

    goToDetails(job) {
      this.$router.push({ name: "JobDetails", params: { job } });
    },

    openDetails(job) {
      this.selectedJob = job;
    },

    infoFields(job) {
      return [
        {
          icon: "mdi-pound",
          label: "Item No.",
          value: job.plantilla_item || "N/A",
        },
        {
          icon: "mdi-domain",
          label: "Department",
          value: this.display(job.unit_department),
        },
        {
          icon: "mdi-map-marker-outline",
          label: "Assignment",
          value: this.display(job.place_assignment),
        },
        {
          icon: "mdi-license",
          label: "Eligibility",
          value: this.display(job.eligibility),
        },
      ];
    },

    sections(job) {
      return [
        {
          title: "Competency",
          icon: "mdi-star-circle-outline",
          html: this.display(job.competency),
        },
        {
          title: "Experience",
          icon: "mdi-briefcase-outline",
          html: this.display(job.experience),
        },
        {
          title: "Education",
          icon: "mdi-school-outline",
          html: this.display(job.education || job.job_posting_content),
        },
        {
          title: "Training",
          icon: "mdi-certificate-outline",
          html: this.display(job.training),
        },
      ];
    },

    display(val) {
      return val ? val : "None Required";
    },
  },
};
</script>

<style scoped>
.hero {
  --brand: #147452;
  --accent: #2fd6a1;
  position: relative;
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 32px 0;
  box-sizing: border-box;
  background: url("../../assets/img/bg_img.png") center / cover no-repeat;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(4, 20, 15, 0.55),
    rgba(0, 0, 0, 0.3) 50%,
    rgba(4, 20, 15, 0.65)
  );
}

/* DEADLINE */
.deadline {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  max-width: 780px;
  margin: 0 16px 4px;
  padding: 10px 22px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: #fff;
  font-size: 15px;
  font-weight: 300;
  line-height: 1.4;
}
.deadline p {
  margin: 0;
}
.deadline strong {
  color: var(--accent);
  font-weight: 600;
}

/* CAROUSEL */
.carousel-container {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
}

.viewport {
  width: 100%;
  padding: 36px 0;
  box-sizing: border-box;
  overflow: hidden;
  -webkit-mask-image: linear-gradient(
    90deg,
    transparent,
    #000 6%,
    #000 94%,
    transparent
  );
  mask-image: linear-gradient(
    90deg,
    transparent,
    #000 6%,
    #000 94%,
    transparent
  );
}

.track {
  display: flex;
  gap: 80px;
  transition: transform 0.55s cubic-bezier(0.22, 0.8, 0.25, 1);
}

/* CARD */
.card {
  flex: 0 0 auto;
  width: min(720px, calc(100vw - 48px));
  height: 640px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 26px 28px 24px;
  border-radius: 24px;
  color: #fff;
  overflow: hidden;
  background: linear-gradient(
    160deg,
    rgba(24, 36, 32, 0.78),
    rgba(10, 16, 14, 0.78)
  );
  border: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  transition: transform 0.5s ease, opacity 0.5s ease, filter 0.5s ease,
    border-color 0.3s ease;

  /* default = hidden */
  opacity: 0;
  transform: scale(0.88);
  pointer-events: none;
}
.card.left,
.card.right {
  opacity: 0.45;
  transform: scale(0.9);
  filter: blur(1.5px);
  pointer-events: auto;
  cursor: pointer;
}
.card.active {
  opacity: 1;
  transform: scale(1);
  filter: none;
  pointer-events: auto;
  border-color: rgba(47, 214, 161, 0.55);
}

/* HEAD */
.card-head {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
}
.eyebrow {
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--accent);
}
.card-head h3 {
  margin: 0;
  font-size: 26px;
  font-weight: 600;
  line-height: 1.2;
}
.chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}
.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
}
.chip--salary {
  background: linear-gradient(135deg, var(--brand), #1b9a6e);
  border-color: transparent;
}
.chip small {
  font-size: 11px;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* BODY */
.body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-right: 4px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
}
.body::-webkit-scrollbar {
  width: 6px;
}
.body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.25);
  border-radius: 6px;
}

.info {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.info-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
}
.info-item .v-icon {
  margin-top: 2px;
}
.info-label {
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.65;
}
.info-value {
  font-size: 14px;
  line-height: 1.35;
  word-break: break-word;
}

.details {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  align-content: start;
}
.section {
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.section h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 6px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--accent);
}
.rich {
  font-size: 14px;
  font-weight: 300;
  line-height: 1.5;
  word-break: break-word;
}
.rich ::v-deep p {
  margin: 0 0 4px;
}
.rich ::v-deep ul,
.rich ::v-deep ol {
  margin: 0;
  padding-left: 18px;
}

/* APPLY */
.apply-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  max-width: 320px;
  margin: 0 auto;
  padding: 13px 24px;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, var(--brand), #1b9a6e);
  color: #fff;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(20, 116, 82, 0.45);
  transition: transform 0.2s ease, filter 0.2s ease;
}
.apply-btn:hover {
  transform: translateY(-2px);
  filter: brightness(1.12);
}
.card:not(.active) .apply-btn {
  pointer-events: none;
}

/* NAV */
.nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.16);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  cursor: pointer;
  transition: background 0.3s ease;
  z-index: 10;
}
.nav-btn:hover {
  background: rgba(255, 255, 255, 0.32);
}
.nav-btn--side {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
}
.nav-btn--side.left {
  left: 16px;
}
.nav-btn--side.right {
  right: 16px;
}
.nav-btn--inline {
  display: none;
  width: 44px;
  height: 44px;
}

/* DOTS */
.controls {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 0 16px;
}
.dots {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 8px;
}
.dot {
  width: 9px;
  height: 9px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  transition: all 0.3s ease;
}
.dot.active {
  width: 28px;
  background: var(--accent);
}

/* EMPTY */
.empty {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px;
  color: #fff;
  text-align: center;
}
.empty p {
  margin: 0;
  font-weight: 300;
}

/* PRIVACY DIALOG */
.privacy-card {
  border-radius: 16px !important;
  overflow: hidden;
}
.privacy-header {
  display: flex;
  align-items: center;
  padding: 20px 24px;
  background: linear-gradient(
    135deg,
    var(--v-primary-base),
    var(--v-primary-darken2)
  );
}
.privacy-modal {
  max-height: 55vh;
  overflow-y: auto;
}
.privacy-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 18px;
}
.privacy-icon {
  flex: 0 0 auto;
  margin-right: 14px;
  margin-top: 2px;
}
.privacy-title {
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 2px;
}
.privacy-item p {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.65);
  text-align: justify;
}

/* TABLET */
@media (max-width: 1024px) {
  .track {
    gap: 48px;
  }
}

/* MOBILE */
@media (max-width: 768px) {
  .track {
    gap: 16px;
  }
  .nav-btn--side {
    display: none;
  }
  .nav-btn--inline {
    display: flex;
  }
  .card {
    height: 600px;
    padding: 20px 18px;
    border-radius: 20px;
  }
  .card-head h3 {
    font-size: 21px;
  }
  .info,
  .details {
    grid-template-columns: 1fr;
  }
  .deadline {
    font-size: 13px;
    border-radius: 18px;
    padding: 10px 16px;
  }
}

@media (max-width: 480px) {
  .viewport {
    padding: 24px 0;
  }
  .apply-btn {
    max-width: none;
  }
}
.dialog-card {
  border-radius: 12px;
  overflow: hidden;
}

.privacy-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 24px;
  color: #fff;
  background: #0e3322; /* green darken-4 */
}
.privacy-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
}
.privacy-head-title {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}
.privacy-head-sub {
  font-size: 13px;
  opacity: 0.85;
}

.privacy-item {
  display: flex;
  gap: 14px;
  margin-bottom: 18px;
}
.privacy-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e8f5e9;
}
.privacy-title {
  font-size: 15px;
  font-weight: 600;
  color: #263238;
}
.privacy-item p {
  margin: 4px 0 0;
  font-size: 14px;
  line-height: 1.55;
  color: #546e7a;
}

.empty-state {
  max-width: 460px;
  margin: 48px auto;
  padding: 40px 32px;
  text-align: center;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 20px;
  box-shadow: 0 8px 24px rgba(30, 138, 57, 0.08);
}
.empty-icon-wrap {
  width: 88px;
  height: 88px;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #e8f5ec;
  box-shadow: 0 0 0 10px rgba(30, 138, 57, 0.06);
}
.empty-title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
}
.empty-text {
  margin: 0 auto 20px;
  max-width: 340px;
  font-size: 14px;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.6);
}
.empty-actions {
  display: flex;
  justify-content: center;
}
</style>
